import { useCallback, type RefCallback } from 'react';

import { registerAnchor, unregisterAnchor, type HintAnchorId } from '@/shared/stores';

/**
 * Bind a DOM element as a hint anchor under `id`. Pass `undefined` to keep
 * the call unconditional on components that only sometimes opt in.
 *
 * Uses React 19's cleanup-return pattern so registration is scoped exactly
 * to the element's lifetime (Strict Mode re-mounts and id swaps included).
 * Multiple components may share an id — the engine spotlights the first
 * registered (mount-ordered) instance.
 */
export function useHintAnchor(id: HintAnchorId | undefined): RefCallback<HTMLElement> {
  return useCallback(
    (element: HTMLElement | null) => {
      if (id === undefined || element === null) return;
      registerAnchor(id, element);
      return () => {
        unregisterAnchor(id, element);
      };
    },
    [id]
  );
}
